/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import type { BundleType } from '#resources/nls/bundle';

import { useTranslationBundle } from '#hooks/UNSAFE_useTranslationBundle';
import { classNames } from '#utils/UNSAFE_classNames';
import { LOADMORE_STYLE_CLASS } from '#PRIVATE_LoadMoreCollection';
import { tableSkeletonStyles, tableSkeletonStylesHC } from './themes/TableSkeletonStyles.css';
import { TableSkeleton } from './TableSkeleton';

type SkeletonRowProps = {
  tableId: string;
  colspan: number;
  hasTabIndex: boolean;
  isShowFocusRing: boolean;
  isEmbedded: boolean;
  isPendingLayout?: boolean;
  isLoadMore?: boolean;
  isHighContrast: boolean;
};

/**
 * TableSkeletonRow renders a set of 'loading' skeletons.
 **/
export function TableSkeletonRow({
  tableId,
  colspan,
  hasTabIndex,
  isShowFocusRing,
  isEmbedded,
  isPendingLayout = false,
  isLoadMore = false,
  isHighContrast
}: SkeletonRowProps) {
  const translations = useTranslationBundle<BundleType>('@oracle/oraclejet-preact');
  const skeletonStyles = isHighContrast ? tableSkeletonStylesHC : tableSkeletonStyles;
  const cellClassArray = [skeletonStyles.cell];
  if (isShowFocusRing) {
    cellClassArray.push(skeletonStyles.focus);
  }
  const cellClasses = classNames(cellClassArray);
  const innerCellClasses = classNames([skeletonStyles.innerCell]);
  const rowClasses = classNames([skeletonStyles.row, skeletonStyles.rowHeight]);
  const firstRowClasses = isPendingLayout
    ? classNames([skeletonStyles.row, skeletonStyles.headerHeight])
    : rowClasses;

  const accCellContent = (
    <div style={'width:1px;height:0px;overflow:hidden;'}>{translations.collection_loading()}</div>
  );

  // JET-75866 - dropdown should only show three skeletons on initial display with collectionTemplate
  // The number of skeleton rows will be determined as follows:
  // 1. if we are rendering load more rows -> 3 skeletons
  // 2. if we are rendering initial loading in embedded mode -> 2 skeletons (specs need 3 rows - 1 header + 2 skeletons)
  // 3. if we are rendering initial loading in non-embedded mode -> 25 skeletons
  const skeletonsCount = isLoadMore ? 3 : isEmbedded ? 2 : 25;

  return (
    <tr class={LOADMORE_STYLE_CLASS} role={'row'}>
      <td
        class={cellClasses}
        colSpan={colspan}
        role={'gridcell'}
        tabIndex={hasTabIndex ? 0 : -1}
        data-oj-cell-type={isPendingLayout ? 'pending' : isLoadMore ? 'loadMore' : 'loading'}
        {...(isPendingLayout
          ? { 'data-oj-table-pending': tableId }
          : isLoadMore
          ? { 'data-oj-table-loadmore': tableId }
          : { 'data-oj-table-loading': tableId })}
        data-oj-table-focusable={tableId}>
        <div class={innerCellClasses}>
          {accCellContent}
          {[...Array(skeletonsCount)].map((_element, index) => (
            <div class={index === 0 ? firstRowClasses : rowClasses}>
              <TableSkeleton height="4x" isHighContrast={isHighContrast} />
            </div>
          ))}
        </div>
      </td>
    </tr>
  );
}
